import type { PageNode } from "../../../shared/src/pages";
import {
  pageUpdateDate,
  pageUpdatesMoreHref,
  type PageUpdate,
  type PageUpdates,
  type PageUpdatesPage,
} from "../../../shared/src/page-updates-runtime";
import { Dialog, command } from "./ui-kit/dialog";
import { Input } from "./ui-kit/forms";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { PenLine } from "lucide-react";
import { PostFeedCard } from "./post-feed-card";
import { PostActionsStatus, postActionsScope, usePostActions } from "./post-actions";
import { PostSocialCard } from "./post-social-card";

type UpdatesNode = Extract<PageNode, { kind: "updates" }>;

function UpdateForm({
  node,
  path,
  item,
}: {
  node: UpdatesNode;
  path: string;
  item?: PageUpdates["items"][number];
}) {
  const id = `${node.id}-${item?.id ?? "new"}`;
  return (
    <form
      action={node.source === "bible" ? "/api/league-bible/updates" : "/api/page-updates/posts"}
      method="post"
      className="drate-updates-form"
    >
      <input type="hidden" name="returnTo" value={path} />
      {item && <input type="hidden" name="id" value={item.id} />}
      {node.source === "posts" && (
        <>
          <input type="hidden" name="access" value={node.access ?? "members"} />
          {node.collectionId && (
            <input type="hidden" name="collectionId" value={node.collectionId} />
          )}
        </>
      )}
      <Input
        id={`${id}-title`}
        name="title"
        label="Headline"
        density="compact"
        placeholder="What changed?"
        value={item?.title}
        maxLength={160}
        required
      />
      <Input
        id={`${id}-body`}
        name="body"
        label={node.bodyLabel}
        density="compact"
        multiline
        rows={5}
        placeholder="Share the changes with your readers."
        value={item?.body}
        maxLength={4000}
        required
      />
      <button className="button button-primary button-small drate-updates-publish" type="submit">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12l4 4v12a2 2 0 0 1-2 2Z M17 21v-8H7v8 M7 3v5h8V3" />
        </svg>
        {node.publishLabel}
      </button>
    </form>
  );
}

type MorePosts = {
  /** The first page these follow; a new one starts over. */
  from: PageUpdates;
  items: PageUpdate[];
  more: boolean;
  status: "idle" | "loading" | "failed";
  /** Heard, not seen: how many posts the feed shows once a page arrives. */
  announcement: string;
};

const morePostsFor = (data: PageUpdates): MorePosts => ({
  from: data,
  items: [],
  more: !!data.more,
  status: "idle",
  announcement: "",
});

/**
 * An infinite feed's further posts: the next page loads well before the reader reaches the end,
 * and again after it, until the creator has no more. Each page is a private, authorized answer,
 * so a member sees the posts they unlocked. A failed page waits for Try again.
 */
function useMorePosts(node: UpdatesNode, data: PageUpdates, enabled: boolean) {
  const [state, setState] = useState(() => morePostsFor(data));
  const current = state.from === data ? state : morePostsFor(data);
  const marker = useRef<HTMLDivElement>(null);
  const request = useRef<AbortController | null>(null);
  const shown = current.items.length ? [...data.items, ...current.items] : data.items;
  const last = shown.at(-1);

  function load() {
    if (!enabled || !last || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setState({ ...current, status: "loading" });
    fetch(pageUpdatesMoreHref(node, last), {
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json() as Promise<PageUpdatesPage>;
      })
      .then((page) =>
        setState((previous) => {
          const base = previous.from === data ? previous : morePostsFor(data);
          const seen = new Set([...data.items, ...base.items].map((item) => item.id));
          const added = page.items.filter((item) => !seen.has(item.id));
          // The running total, so each page reads as news even when pages are the same size.
          return {
            ...base,
            items: [...base.items, ...added],
            more: page.more && added.length > 0,
            status: "idle",
            announcement: `${seen.size + added.length} posts shown`,
          };
        }),
      )
      .catch(() => {
        if (!controller.signal.aborted)
          setState((previous) => ({ ...previous, status: "failed", announcement: "" }));
      })
      .finally(() => {
        if (request.current === controller) request.current = null;
      });
  }

  // Watches the end of the feed from a screen and a half away; once a page arrives, a marker
  // still in range asks for the next straight away.
  const watching = enabled && current.more && current.status === "idle";
  useEffect(() => {
    const element = marker.current;
    if (!watching || !element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load();
      },
      { rootMargin: "0px 0px 150% 0px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
    // `load` reads this render's last post, which the effect follows by its id.
  }, [watching, last?.id, data]);

  useEffect(() => () => request.current?.abort(), []);

  return {
    shown,
    marker,
    retry: load,
    pending: enabled && (current.more || current.status === "failed"),
    status: current.status,
    announcement: current.announcement,
  };
}

/** A native, access-checked feed of full updates; authored pages choose its source and position. */
export function UpdatesFeed({
  node,
  data,
  path,
  creatorName,
  linkTo,
  canvas,
  ownerPost,
}: {
  node: UpdatesNode;
  data: PageUpdates;
  path: string;
  creatorName?: string;
  linkTo?: (href: string) => string;
  canvas?: boolean;
  /** The owner's private view: where they write a post, for an empty feed of posts. */
  ownerPost?: string;
}) {
  const search = useLocation({ select: (location) => location.searchStr });
  const params = new URLSearchParams(search);
  const posts = node.presentation === "posts" && node.source === "posts";
  const social = posts && node.postStyle === "social";
  const items = useRef<HTMLDivElement>(null);
  // Only a published page's own feed asks for more; the canvas shows its first page.
  const more = useMorePosts(node, data, !!node.infinite && node.source === "posts" && !canvas);
  usePostActions(items, social, more.shown.map((item) => item.id).join(","));
  return (
    <div className="drate-updates-layout" data-owner={data.canPublish ? "true" : undefined}>
      <div className="drate-updates-feed">
        {data.canPublish && (params.has("error") || params.has("notice")) && (
          <p role="status" className="drate-updates-notice">
            {params.has("error")
              ? "We couldn’t save that update. Check the fields and try again."
              : params.get("notice") === "removed"
                ? "Update removed."
                : "Update published."}
          </p>
        )}
        {node.heading && (
          <h2 className="drate-updates-heading">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              aria-hidden="true"
            >
              <path d="M12 5v16M12 5C9 2 5 2 2 3v16c3-1 7-1 10 2 3-3 7-3 10-2V3c-3-1-7-1-10 2" />
            </svg>
            {node.heading}
          </h2>
        )}
        {data.items.length ? (
          <div
            ref={items}
            className={posts ? "drate-post-items" : "drate-update-items"}
            {...(social &&
              postActionsScope(
                linkTo ? linkTo("/sign-in") : "/sign-in",
                linkTo ? linkTo("/join") : "/join",
              ))}
          >
            {social && <PostActionsStatus />}
            {more.shown.map((item, index) =>
              social ? (
                <PostSocialCard
                  key={item.id}
                  item={item}
                  node={node}
                  linkTo={linkTo}
                  canvas={canvas}
                  editHref={
                    data.canPublish
                      ? data.manageHref?.replace(
                          "/content?",
                          `/content/${encodeURIComponent(item.id)}?`,
                        )
                      : undefined
                  }
                />
              ) : posts ? (
                <PostFeedCard
                  key={item.id}
                  item={item}
                  node={node}
                  creatorName={creatorName}
                  linkTo={linkTo}
                  canvas={canvas}
                  editHref={
                    data.canPublish
                      ? data.manageHref?.replace(
                          "/content?",
                          `/content/${encodeURIComponent(item.id)}?`,
                        )
                      : undefined
                  }
                />
              ) : (
                <article key={item.id} className="drate-update-card" data-update-id={item.id}>
                  <div className="drate-update-top">
                    <h3>{item.title}</h3>
                    {!index && node.latestLabel && (
                      <span className="drate-update-latest">{node.latestLabel}</span>
                    )}
                    {node.dateFormat !== "hidden" && item.date && (
                      <time dateTime={item.date}>Updated {pageUpdateDate(item.date, node)}</time>
                    )}
                  </div>
                  {item.locked ? (
                    <p className="drate-update-gate">
                      <a href={item.url ?? "/join"}>
                        Members only · Sign in or become a member to read
                      </a>
                    </p>
                  ) : item.story ? (
                    <div
                      className="drate-update-body story"
                      dangerouslySetInnerHTML={{ __html: item.story }}
                    />
                  ) : (
                    <p className="drate-update-body">{item.body}</p>
                  )}
                  {data.canPublish && (
                    <div className="drate-update-actions">
                      {node.source === "bible" ? (
                        <>
                          <button
                            type="button"
                            className="button button-quiet button-small"
                            {...command(`${node.id}-${item.id}-edit`, "show-modal")}
                          >
                            ✎ Edit
                          </button>
                          <Dialog id={`${node.id}-${item.id}-edit`} title="Edit update">
                            <UpdateForm node={node} path={path} item={item} />
                          </Dialog>
                        </>
                      ) : (
                        <a
                          className="button button-quiet button-small"
                          href={data.manageHref?.replace(
                            "/content?",
                            `/content/${encodeURIComponent(item.id)}?`,
                          )}
                          data-reload=""
                        >
                          ✎ Edit
                        </a>
                      )}
                      <button
                        type="button"
                        className="button button-quiet button-small"
                        {...command(`${node.id}-${item.id}-remove`, "show-modal")}
                      >
                        Remove
                      </button>
                      <Dialog
                        id={`${node.id}-${item.id}-remove`}
                        title="Remove update"
                        description={`Remove “${item.title}” from the live feed?${node.source === "posts" ? " Its draft will be kept." : ""}`}
                      >
                        <form
                          action={
                            node.source === "bible"
                              ? `/api/league-bible/updates/${item.id}/delete`
                              : `/api/page-updates/posts/${item.id}/unpublish`
                          }
                          method="post"
                        >
                          <input type="hidden" name="returnTo" value={path} />
                          <button type="submit" className="button button-primary">
                            Remove update
                          </button>
                        </form>
                      </Dialog>
                    </div>
                  )}
                </article>
              ),
            )}
          </div>
        ) : ownerPost && node.source === "posts" ? (
          <div className="drate-owner-empty">
            <PenLine size={20} strokeWidth={1.75} aria-hidden="true" />
            <strong>No posts yet</strong>
            <p>Posts you publish show up here.</p>
            <a className="button button-primary button-small" href={ownerPost}>
              Write a post
            </a>
          </div>
        ) : node.emptyText ? (
          <p className="drate-updates-empty">{node.emptyText}</p>
        ) : null}
        {more.pending && (
          <div ref={more.marker} className="drate-updates-more" data-status={more.status}>
            {more.status === "loading" && <span>Loading more posts…</span>}
            {more.status === "failed" && (
              <>
                <span>We couldn’t load more posts.</span>
                <button
                  type="button"
                  className="button button-outline button-small"
                  onClick={more.retry}
                >
                  Try again
                </button>
              </>
            )}
          </div>
        )}
        {node.infinite && (
          <p className="sr-only" role="status">
            {more.announcement}
          </p>
        )}
      </div>
      {data.canPublish && (
        <aside className="drate-updates-owner">
          <h3>
            <span aria-hidden="true">+</span> {node.ownerHeading}
          </h3>
          <UpdateForm node={node} path={path} />
        </aside>
      )}
    </div>
  );
}
